import {ShopPageTitle} from "../shop/_components/ShopPageTitle";
import React, {useMemo, useId, useState} from "react";
import {useAuth} from "../../context";
import {useAddressList} from "../backend2/address/useAddress";
import {useCarrierList} from "../backend2/admin/carrier/useCarrier";
import {Button, Col, Form, Modal, Row,} from "react-bootstrap";
import {Link, Navigate, useNavigate} from "react-router-dom";
import {CheckoutProgress} from "../shop/_components/CheckoutProgress";
import {useBasket} from "../shop/useShop";
import {Formik, Form as FormikForm, Field} from "formik";
import * as yup from "yup";
import {OurClearAlert} from "../_components";
import {OrderSummary} from "../shop/_components/OrderSummary";


export function ShippingAddress() {
    const {bskToken} = useAuth()

    const {basket, editArticle, errorArticle, loadingArticle, clearErrorArticle} = useBasket();
    const {list: addressList, loadingList: addressLoadingList, errorList: addressErrorList, clearErrorList: addressClearErrorList} = useAddressList(100)
    const {list: carrierList2, loadingList: loadingCarrierList, errorList: errorCarrierList, clearErrorList: clearErrorCarrierList} = useCarrierList()

    const freeShipping = basket?.articles.every(article => article.is_digital === true)
    const carrierList = freeShipping ? carrierList2 : carrierList2.filter(item => item.name.toLowerCase() !== "free")

    const addressId = useMemo(() => basket?.address_id ? basket?.address_id : addressList.find(item => item.main)?.id, [addressList, basket])
    const billingId = useMemo(() => basket?.billing_id ? basket?.billing_id : addressId, [addressId, basket])

    const availableCountryCodes = useMemo(() => [...new Set(carrierList.flatMap(c => c.countries.map(country => country.code)))], [carrierList])
    const availableCountryNames = useMemo(() => [...new Set(carrierList.flatMap(c => c.countries.map(country => country.name)))], [carrierList])

    const basketTemp = useMemo(() => ({...basket, address_id: addressId, billing_id: billingId}), [basket, addressId, billingId])
    const loading = addressLoadingList || loadingCarrierList

    if (!bskToken || (loadingArticle === false && basket?.articles.length === 0)) {
        return <Navigate to="/shop"/>
    }

    const goBack = "/shop/cart"
    const {userId} = useAuth()
    const navigate = useNavigate();

    const validationSchema = yup.object().shape({
        address_id: yup.string()
            .test(
                "required",
                "Please select or add a valid shipping address.",
                value => {
                    const selectedAddress = addressList.find(item => item.id === value)
                    return selectedAddress && availableCountryCodes.includes(selectedAddress.country)
                }
            ),
        billing_id: yup.string()
            .test("required",
                "Select an address for billing.",
                value => {
                    return value != null && value !== ""
                }),
    })

    function handleSubmit(values, actions) {
        editArticle(values)
            .then(() => navigate("/checkout/delivery"))
            .catch(console.warn)
            .finally(() => actions.setSubmitting(false))
    }

    return <>
        <OurClearAlert variant="danger" msg={errorArticle} onClear={clearErrorArticle}/>
        <OurClearAlert variant="danger" msg={addressErrorList} onClear={addressClearErrorList}/>
        <OurClearAlert variant="danger" msg={errorCarrierList} onClear={clearErrorCarrierList}/>

        <CheckoutProgress/>

        <ShopPageTitle title="Shipping Address"/>

        <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={basketTemp} enableReinitialize={true}>
            {({isSubmitting, values, setFieldValue, errors, touched}) => (
                <FormikForm>

                    <Row>
                        <Col md={8}>
                            <div> Select an existing address or <Link to={`/backend/address/${userId}/add`}>add a new one</Link>:</div>
                            {loading ?
                                <div>Loading addresses...</div>
                                :
                                addressList.length > 0 ?
                                    <>
                                        <AddressList list={addressList} availableCountries={availableCountryCodes} selectedAddress={values.address_id}
                                                     setFieldValue={value => setFieldValue("address_id", value)} showEdit/>
                                        {errors.address_id && touched.address_id && <div className="text-danger">{errors.address_id}</div>}

                                        <BillingCheckbox name="billing_id" label="Use the same address for billing" addressId={values.address_id}
                                                         billing_id={values.billing_id}
                                                         setFieldValue={setFieldValue}/>

                                        {values.billing_id !== values.address_id && (
                                            <>
                                                <div>Select a billing address:</div>
                                                <AddressList list={addressList} availableCountries={addressList.map(c => c.country)} selectedAddress={values.billing_id}
                                                             setFieldValue={value => setFieldValue("billing_id", value)}/>
                                                {errors.billing_id && touched.billing_id && (
                                                    <div className="text-danger">{errors.billing_id}</div>
                                                )}
                                            </>
                                        )}

                                    </>
                                    :
                                    <>
                                        {/*<div>No shipping address found. <Link to={`/backend/address/${userId}/add`}>Please add a new address</Link>.</div>*/}
                                        <div>No shipping address found.</div>
                                        {errors.address_id && touched.address_id && (
                                            <div className="text-danger">{errors.address_id}</div>
                                        )}
                                    </>
                            }
                            <ModalAvailableCountries countries={availableCountryNames} loading={loadingCarrierList}/>
                        </Col>

                        <Col>
                            <OrderSummary basket={basketTemp} disabled={isSubmitting} className="d-none d-md-block"/>
                            <div className="d-flex justify-content-end gap-2 my-2">
                                <Button as={Link} variant="link" to={goBack}>Go back</Button>
                                <Button variant="success" type="submit" className="d-md-none" disabled={isSubmitting}>Continue</Button>
                            </div>
                        </Col>
                    </Row>

                </FormikForm>
            )}
        </Formik>
    </>
}


function AddressList({list, availableCountries, selectedAddress, setFieldValue, showEdit}) {
    const {userId} = useAuth()
    // const fieldName = type === "shipping" ? "address_id" : "billing_id"
    return (
        <>
            {list.map((item, index) => (
                <div key={index} className="d-flex flex-wrap align-items-top gap-2 my-2">
                    <Form.Check
                        type="radio"
                        id={useId()}
                        disabled={!availableCountries.includes(item.country)}
                        checked={selectedAddress === item.id}
                        onChange={() => setFieldValue(item.id)}
                        label={<div className="d-flex flex-column">
                            <div>{`${item.name}, ${item.line1}`}</div>
                            <div>{`${item.city} ${item.zip}, ${item.country}`}</div>
                            <div>{`+${item.prefix}${item.phone}`}</div>
                            {showEdit && availableCountries.includes(item.country) && (
                                <Link to={`/backend/address/${userId}/${item.id}`}>Edit</Link>
                            )}
                        </div>}
                    />
                </div>
            ))}
        </>
    );
}

export function BillingCheckbox({name, label, addressId, billing_id, setFieldValue}) {
    const type = "checkbox"

    return <Field name={name} type={type}>
        {({field}) => <Form.Check id={useId()} {...field} type={type} label={label} className="my-4" checked={billing_id === addressId}
                                  onChange={e => setFieldValue(name, e.target.checked ? addressId : "")}/>
        }
    </Field>
}

function ModalAvailableCountries({countries, loading}) {
    const [show, setShow] = useState(false)

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    return (
        <>
            <Button variant="link" className="p-0" onClick={handleShow}>
                <span>Delivery countries</span>
            </Button>

            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Delivery Countries</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap">
                        {loading ?
                            <span>One moment please...</span>
                            :
                            countries ?
                                countries.map((item, index) => <div key={index} className="me-2">{item}</div>)
                                :
                                <span>No countries available</span>
                        }
                    </div>
                    <div className="mt-4">
                        Can't find your country? <Link to="/contact-us" className="link-underline link-underline-opacity-0 link-underline-opacity-75-hover">Reach out to
                        us</Link>.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    )
}